import { useState } from "react";
import { Link } from "react-router-dom";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db7jes73hiaolq192i20";

const missions = [
  {
    icon: "ri-focus-3-line",
    title: "我们的使命",
    desc: "让每一位影视爱好者都能便捷地发现、了解并欣赏优质的电影与电视剧内容。",
  },
  {
    icon: "ri-service-line",
    title: "我们的服务",
    desc: "提供清晰的分类浏览、灵活的筛选排序、快速的关键词检索与智能的相关推荐。",
  },
  {
    icon: "ri-shield-star-line",
    title: "我们的坚持",
    desc: "坚持内容真实、信息完整、界面清爽，持续优化浏览与观看体验。",
  },
];

export default function About() {
  useDocumentMeta({
    title: "关于我们 - 51无敌电影网",
    description:
      "了解51无敌电影网的定位、服务范围与联系方式。我们致力于为影视爱好者提供最新电影电视剧的浏览、筛选与观看入口。",
    keywords: "关于我们,51无敌电影网,影视平台,联系方式,免责声明",
  });

  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setSubmitting(true);
    setStatus("idle");
    setFormError("");

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === "website_alt") return;
        const str = String(value).trim();
        if (str) payload.append(key, str);
      });

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim() ? serverMsg : "提交失败，请稍后重试"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络后重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <section className="w-full px-4 md:px-6 pt-10 md:pt-14 pb-8 bg-background-100">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-semibold">
            <i className="ri-information-line"></i>
            关于我们
          </span>
          <h1 className="mt-4 text-2xl md:text-4xl font-heading font-bold text-foreground-950">
            51无敌电影网
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
            51无敌电影网是一个聚焦最新电影与电视剧在线播放与信息展示的影视平台，
            面向广大影视爱好者、追剧党，提供便捷的分类浏览、快速筛选与智能推荐体验。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {missions.map((item) => (
            <div
              key={item.title}
              className="rounded-lg bg-background-50 border border-background-200 p-6"
            >
              <span className="w-11 h-11 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <h4 className="mt-4 text-base font-heading font-semibold text-foreground-900">
                {item.title}
              </h4>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-6 pb-10 md:pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-7">
            <h4 className="flex items-center gap-2 text-lg font-heading font-semibold text-foreground-900">
              <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                <i className="ri-copyright-line"></i>
              </span>
              版权声明
            </h4>
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
              本网站为影视内容展示演示平台，所展示的影视信息（含名称、简介、演职人员等）仅用于页面演示与学习交流，
              不代表任何商业用途。相关内容的版权归各自权利人所有。
            </p>
          </div>

          <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-7">
            <h4 className="flex items-center gap-2 text-lg font-heading font-semibold text-foreground-900">
              <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center">
                <i className="ri-error-warning-line"></i>
              </span>
              免责声明
            </h4>
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
              本网站不提供真实的在线播放服务，播放入口仅为前端演示示范区，不涉及任何实际数据或接口。
              若本站内容无意中侵犯了您的权益，请通过下方联系方式告知，我们将在核实后及时处理。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 pb-14 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
          <div className="lg:col-span-2">
            <h4 className="text-lg font-heading font-semibold text-foreground-900">联系方式</h4>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              无论是内容建议、合作咨询还是侵权反馈，都欢迎随时联系我们。
              我们会认真阅读每一条留言，并尽快回复。
            </p>
            <ul className="mt-6 space-y-4">
              <li className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400 font-label">邮箱</p>
                  <p className="text-sm text-foreground-800">service@51wudi.com</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400 font-label">服务时间</p>
                  <p className="text-sm text-foreground-800">周一至周五 09:00 - 18:00</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-600 flex items-center justify-center">
                  <i className="ri-map-pin-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400 font-label">地址</p>
                  <p className="text-sm text-foreground-800">北京市朝阳区影视产业园 A 座</p>
                </div>
              </li>
            </ul>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 text-sm font-label text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页继续浏览
            </Link>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-7">
              <h4 className="text-lg font-heading font-semibold text-foreground-900">给我们留言</h4>
              <p className="mt-2 text-sm text-foreground-500">填写下方表单，我们会尽快与你联系</p>

              {status === "success" && (
                <div className="mt-5 flex items-start gap-2 rounded-lg bg-primary-50 p-3.5 text-sm text-primary-700">
                  <i className="ri-checkbox-circle-line text-lg mt-0.5"></i>
                  <p>留言提交成功，感谢你的反馈，我们会尽快查看并回复！</p>
                </div>
              )}
              {status === "error" && (
                <div className="mt-5 flex items-start gap-2 rounded-lg bg-accent-50 p-3.5 text-sm text-accent-800">
                  <i className="ri-error-warning-line text-lg mt-0.5"></i>
                  <p>{formError}</p>
                </div>
              )}

              <form
                id="about-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-5 space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-label text-foreground-500 mb-1.5">
                      你的称呼
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      placeholder="请输入姓名或昵称"
                      className="w-full h-11 px-3.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-label text-foreground-500 mb-1.5">
                      联系邮箱
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      placeholder="请输入邮箱地址"
                      className="w-full h-11 px-3.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-topic" className="block text-xs font-label text-foreground-500 mb-1.5">
                    留言类型
                  </label>
                  <select
                    id="contact-topic"
                    name="topic"
                    defaultValue="内容建议"
                    className="w-full h-11 px-3.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="侵权反馈">侵权反馈</option>
                    <option value="其他问题">其他问题</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-xs font-label text-foreground-500 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你的问题或建议（不超过500字）"
                    className="w-full px-3.5 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
                  />
                </div>

                <input
                  className="form-honeypot-guard"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto px-8 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "提交中..." : "提交留言"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}